import { Endpoint } from '@shared/types'
import axios, { AxiosError, AxiosRequestConfig, CanceledError } from 'axios'
import { StatusCodes } from 'http-status-codes'
import * as R from 'remeda'

import { sendEndpointRetryEventToAnalytics, sendErrorEventToAnalytics } from '../../analytics'

export interface CustomRequestConfig {
  maxRetries?: number
  retryDelayMs?: number
  maxRetryAfterMs?: number
  abortSignal?: AbortSignal
}

export interface CustomAxiosRequestConfig extends AxiosRequestConfig {
  customRequestConfig?: CustomRequestConfig
}

export interface EndpointParameters<
  Request = void,
  QueryParams extends Record<string, string> = Record<string, string>,
  PathParams extends Record<string, string> = Record<string, string>,
> {
  queryParams?: QueryParams
  pathParams?: PathParams
  requestPayload?: Request
  requestConfig?: CustomRequestConfig
  skipCacheBusting?: boolean
  abortSignal?: AbortSignal
}

export const getCustomRequestConfig = (error: AxiosError): CustomRequestConfig | undefined => {
  return (error.config as CustomAxiosRequestConfig)?.customRequestConfig
}

const buildAxiosConfig = <
  Response,
  Request,
  QueryParams extends Record<string, string>,
  PathParams extends Record<string, string>,
>(
  endpoint: Endpoint<Response, Request, QueryParams, PathParams>,
  params: EndpointParameters<Request, QueryParams, PathParams>,
) => {
  const { requestConfig, skipCacheBusting, abortSignal, queryParams } = params
  const axiosConfig: CustomAxiosRequestConfig = {
    params: { ...(skipCacheBusting ? {} : { ts: Date.now() }), ...queryParams },
    signal: abortSignal,
  }
  if (requestConfig) {
    axiosConfig.customRequestConfig = requestConfig
    if (requestConfig.maxRetries !== undefined && requestConfig.maxRetries >= 0) {
      axiosConfig['axios-retry'] = { retries: requestConfig.maxRetries }
      axiosConfig['axios-retry'].onRetry = (retryCount, error) =>
        sendEndpointRetryEventToAnalytics(retryCount, endpoint?.method, endpoint?.path, error)
    }
    if (requestConfig?.abortSignal) {
      axiosConfig['signal'] = requestConfig.abortSignal
    }
  }

  return axiosConfig
}

const buildRequestUrl = <
  Response,
  Request,
  QueryParams extends Record<string, string>,
  PathParams extends Record<string, string>,
>({
  endpoint,
  pathParams,
}: {
  endpoint: Endpoint<Response, Request, QueryParams, PathParams>
  pathParams: Record<string, string> | undefined
}) => {
  let url = endpoint.path

  if (pathParams) {
    for (const pathParam of Object.entries<string>(pathParams)) {
      url = url.replace(`{${pathParam[0]}}`, pathParam[1])
    }
  }

  return url
}

export const callEndpoint = async <
  Response,
  Request,
  QueryParams extends Record<string, string>,
  PathParams extends Record<string, string>,
>(
  endpoint: Endpoint<Response, Request, QueryParams, PathParams>,
  params: EndpointParameters<Request, QueryParams, PathParams> = {},
): Promise<Response> => {
  const { pathParams, requestPayload } = params
  const url = buildRequestUrl({ endpoint, pathParams })
  const axiosConfig = buildAxiosConfig(endpoint, params)

  const request = {
    get: () => axios.get<Response>(url, axiosConfig),
    put: () => axios.put<Response>(url, requestPayload, axiosConfig),
    post: () => axios.post<Response>(url, requestPayload, axiosConfig),
    delete: () => axios.delete<Response>(url, axiosConfig),
  }[endpoint.method]

  try {
    const { data } = await request()
    return data
  } catch (error) {
    if (error instanceof CanceledError) {
      throw error
    }

    if (R.isError(error)) {
      sendErrorEventToAnalytics(`${endpoint.method} ${endpoint.path} failed: ${error.message}`, error)
    } else {
      sendErrorEventToAnalytics(`${endpoint.method} ${endpoint.path} failed: ${JSON.stringify(error)}`)
    }

    if (error instanceof AxiosError && error.status !== StatusCodes.GATEWAY_TIMEOUT && error?.response?.data) {
      throw error.response.data
    }
    throw error
  }
}
